import React from 'react';
import {Alert} from 'reactstrap';
import {AlertTriangle, Clock, Lock, XCircle} from 'lucide-react';
import {Link} from 'react-router-dom';
import useGetSubscriptionStatus from '../../api/customers/useGetSubscriptionStatus';
import {useAuthStateManager} from '../../hooks/useAuthStateManager';
import {SubscriptionStatusModel} from '../../types';
import {formatDateSafe} from '../../lib/dateUtils';
import {RoutePaths} from '../../RoutePaths';

/**
 * Global banner that warns about subscription issues:
 * - Enforcement (read-only / billing-only) state, with grace-period warnings
 * - Trial expiring soon (< 14 days)
 * - Subscription past due
 * - Subscription canceled
 *
 * Only renders for authenticated users with a customer context.
 */
const SubscriptionBanner: React.FC = () => {
    const authStateManager = useAuthStateManager();

    // Only show for authenticated users with a customer login
    if (!authStateManager.isAuthenticated || !authStateManager.customerId) {
        return null;
    }

    return <SubscriptionBannerInner />;
};

const SubscriptionBannerInner: React.FC = () => {
    const {data: subscription, isLoading} = useGetSubscriptionStatus();

    if (isLoading || !subscription) return null;

    // Billing is off in this environment, or this customer sits outside it:
    // nothing to warn about.
    if (subscription.billingMode === 'none' || subscription.billingExempt) {
        return null;
    }

    // Enforcement banners take priority when enforcement is applied
    if (subscription.enforcementActive) {
        const enforcementBanner = (
            <EnforcementBanner subscription={subscription} />
        );
        if (
            subscription.enforcementMode !== 'Full' ||
            subscription.enforcementReason === 'past_due_grace'
        ) {
            return enforcementBanner;
        }
    }

    return (
        <>
            <SandboxEnforcementNote subscription={subscription} />
            <StatusBanner subscription={subscription} />
        </>
    );
};

const enforcementReasonLabels: Record<string, string> = {
    override: 'override active',
    subscribed: 'subscribed',
    trial: 'trial',
    past_due_grace: 'payment past due',
    past_due: 'payment past due',
    canceled: 'subscription canceled',
    paused: 'subscription paused',
    inactive: 'account inactive',
    trial_expired: 'trial expired',
    no_subscription: 'no subscription'
};

const reasonLabel = (reason: string): string =>
    enforcementReasonLabels[reason] ?? reason;

const EnforcementBanner: React.FC<{
    subscription: SubscriptionStatusModel;
}> = ({subscription}) => {
    const {enforcementMode, enforcementReason, gracedUntil} = subscription;

    // During the past-due grace period access is still full — amber warning
    if (enforcementReason === 'past_due_grace') {
        return (
            <Alert
                color="warning"
                className="mb-0 rounded-0 py-2 text-center border-start-0 border-end-0"
                fade={false}
            >
                <AlertTriangle size={16} className="me-2" />
                Your payment is past due. You have full access until{' '}
                <strong>{formatBannerDate(gracedUntil)}</strong>. Please{' '}
                <Link to={RoutePaths.settings.billing()} className="alert-link">
                    update your payment method
                </Link>{' '}
                to avoid your account becoming read-only.
            </Alert>
        );
    }

    if (enforcementMode === 'ReadOnly') {
        const showGracedUntil =
            gracedUntil !== null &&
            (enforcementReason === 'past_due' ||
                enforcementReason === 'past_due_grace');
        return (
            <Alert
                color="danger"
                className="mb-0 rounded-0 py-2 text-center border-start-0 border-end-0"
                fade={false}
            >
                <Lock size={16} className="me-2" />
                Your account is read-only ({reasonLabel(enforcementReason)}).
                {showGracedUntil && (
                    <>
                        {' '}
                        Full access ended on{' '}
                        <strong>{formatBannerDate(gracedUntil)}</strong>.
                    </>
                )}{' '}
                <Link to={RoutePaths.settings.billing()} className="alert-link">
                    Resolve billing
                </Link>{' '}
                to re-enable changes.
            </Alert>
        );
    }

    if (enforcementMode === 'BillingOnly') {
        return (
            <Alert
                color="danger"
                className="mb-0 rounded-0 py-2 text-center border-start-0 border-end-0"
                fade={false}
            >
                <XCircle size={16} className="me-2" />
                Your account is suspended ({reasonLabel(
                    enforcementReason
                )}).{' '}
                <Link to={RoutePaths.settings.billing()} className="alert-link">
                    Go to billing
                </Link>{' '}
                to restore access.
            </Alert>
        );
    }

    return null;
};

/**
 * When running against the Paddle sandbox, enforcement is computed but not
 * applied — show testers what the enforcement state would be.
 */
const SandboxEnforcementNote: React.FC<{
    subscription: SubscriptionStatusModel;
}> = ({subscription}) => {
    if (subscription.enforcementActive) return null;
    if (subscription.enforcementMode === 'Full') return null;

    return (
        <div className="bg-light text-muted small text-center py-1 border-bottom">
            (sandbox: enforcement inactive, would be{' '}
            {subscription.enforcementMode})
        </div>
    );
};

const StatusBanner: React.FC<{subscription: SubscriptionStatusModel}> = ({
    subscription
}) => {
    // A SysAdmin override grants full access regardless of billing state, so
    // the underlying trial / past-due / canceled status is not the customer's
    // problem while it is in force.
    if (subscription.overrideActive) return null;

    // Billing went live while this customer was using Certavue free: say so,
    // for the whole of the trial they were given, instead of the ordinary
    // countdown. Cleared once they subscribe.
    if (
        subscription.billingTransitionAnnouncedAt &&
        subscription.status === 'local_trial' &&
        subscription.trialDaysRemaining !== null &&
        subscription.trialDaysRemaining > 0
    ) {
        return (
            <Alert
                color="info"
                className="mb-0 rounded-0 py-2 text-center border-start-0 border-end-0"
                fade={false}
            >
                <Clock size={16} className="me-2" />
                Certavue is moving to paid subscriptions. Thank you for being
                part of the beta: you have full access until{' '}
                <strong>
                    {formatBannerDate(subscription.trialEndsAt)}
                </strong>.{' '}
                <Link to={RoutePaths.settings.billing()} className="alert-link">
                    Set up your subscription
                </Link>{' '}
                to keep it.
            </Alert>
        );
    }

    // Trial expiring soon (< 14 days remaining)
    if (
        (subscription.status === 'local_trial' ||
            subscription.status === 'trialing') &&
        subscription.trialDaysRemaining !== null &&
        subscription.trialDaysRemaining <= 14
    ) {
        return (
            <Alert
                color="info"
                className="mb-0 rounded-0 py-2 text-center border-start-0 border-end-0"
                fade={false}
            >
                <Clock size={16} className="me-2" />
                Your trial expires in{' '}
                <strong>
                    {subscription.trialDaysRemaining} day
                    {subscription.trialDaysRemaining !== 1 ? 's' : ''}
                </strong>
                .{' '}
                <Link to={RoutePaths.settings.billing()} className="alert-link">
                    Subscribe now
                </Link>{' '}
                to continue using Certavue.
            </Alert>
        );
    }

    // Past due
    if (subscription.status === 'past_due') {
        return (
            <Alert
                color="warning"
                className="mb-0 rounded-0 py-2 text-center border-start-0 border-end-0"
                fade={false}
            >
                <AlertTriangle size={16} className="me-2" />
                Your payment is past due. Please{' '}
                <Link to={RoutePaths.settings.billing()} className="alert-link">
                    update your payment method
                </Link>{' '}
                to avoid service interruption.
            </Alert>
        );
    }

    // Canceled
    if (subscription.status === 'canceled') {
        return (
            <Alert
                color="danger"
                className="mb-0 rounded-0 py-2 text-center border-start-0 border-end-0"
                fade={false}
            >
                <XCircle size={16} className="me-2" />
                Your subscription has been canceled.{' '}
                <Link to={RoutePaths.settings.billing()} className="alert-link">
                    Resubscribe
                </Link>{' '}
                to regain full access.
            </Alert>
        );
    }

    // No subscription at all (and not in trial)
    if (subscription.status === 'none') {
        return (
            <Alert
                color="warning"
                className="mb-0 rounded-0 py-2 text-center border-start-0 border-end-0"
                fade={false}
            >
                <AlertTriangle size={16} className="me-2" />
                You don't have an active subscription.{' '}
                <Link to={RoutePaths.settings.billing()} className="alert-link">
                    Subscribe now
                </Link>{' '}
                to access all features.
            </Alert>
        );
    }

    return null;
};

function formatBannerDate(dateStr: string | null): string {
    if (!dateStr) return 'the end of your grace period';
    return (
        formatDateSafe(dateStr, undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        }) || dateStr
    );
}

export default SubscriptionBanner;
